import React, { useState, useEffect } from "react";
import { FaBolt, FaUsers } from "react-icons/fa";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import DatePicker from "../modal/datepicker";
import TimePicker from "../modal/timepicker";
import api from "../services/api";
import { toast } from "react-toastify";
import { AttendanceStatus, AttendanceStatusFormOptions } from "../../constants/constants";
import "./attendance.css";

const BulkAttendanceModal = ({ onSave, onClose }) => {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [globalStatus, setGlobalStatus] = useState(AttendanceStatus.PRESENT);
  const [globalCheckIn, setGlobalCheckIn] = useState("09:00");
  const [globalCheckOut, setGlobalCheckOut] = useState("18:00");

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const res = await api.get("dropdowns/employees/");
        const list = res.data.data || res.data || [];
        setRows(
          list.map((emp) => ({
            employee_id: emp.id,
            emp_id: emp.emp_id,
            name: emp.full_name || emp.name || emp.emp_id,
            selected: true,
            status: AttendanceStatus.PRESENT,
            check_in_time: "09:00",
            check_out_time: "18:00",
            remarks: "",
          }))
        );
      } catch (err) {
        toast.error("Failed to load employees list");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const handleSelectAll = (checked) => {
    setRows(rows.map((r) => ({ ...r, selected: checked })));
  };

  const applyStatusToAll = () => {
    setRows(rows.map((r) => (r.selected ? { ...r, status: globalStatus } : r)));
    toast.info(`Applied status to selected rows`);
  };

  const applyTimesToAll = () => {
    setRows(
      rows.map((r) =>
        r.selected
          ? {
              ...r,
              check_in_time: globalCheckIn,
              check_out_time: globalCheckOut,
            }
          : r
      )
    );
    toast.info("Applied check-in/out times to selected rows");
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const handleSubmit = async () => {
    const selectedRows = rows.filter((r) => r.selected);
    if (selectedRows.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }
    if (selectedRows.length > 200) {
      toast.error("Cannot mark more than 200 records at once");
      return;
    }

    let hasValidationError = false;
    for (const r of selectedRows) {
      const needsTime = [AttendanceStatus.PRESENT, AttendanceStatus.WORK_FROM_HOME, AttendanceStatus.HALF_DAY].includes(r.status);
      if (needsTime && !r.check_in_time) {
        toast.error(`Check-in time is required for ${r.name} (${r.status})`);
        hasValidationError = true;
        break;
      }
    }
    if (hasValidationError) return;

    const records = selectedRows.map((r) => {
      const needsTime = [AttendanceStatus.PRESENT, AttendanceStatus.WORK_FROM_HOME, AttendanceStatus.HALF_DAY].includes(r.status);
      const cleanIn = needsTime && r.check_in_time ? (r.check_in_time.length === 5 ? `${r.check_in_time}:00` : r.check_in_time) : null;
      const cleanOut = needsTime && r.check_out_time ? (r.check_out_time.length === 5 ? `${r.check_out_time}:00` : r.check_out_time) : null;
      return {
        employee: r.employee_id,
        date: date,
        status: r.status,
        check_in_time: cleanIn,
        check_out_time: cleanOut,
        remarks: r.remarks,
      };
    });

    setSubmitting(true);
    try {
      await onSave({ records });
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected = rows.length > 0 && rows.every((r) => r.selected);
  const selectedCount = rows.filter((r) => r.selected).length;

  return (
    <Modal
      title="Bulk Mark Attendance"
      onClose={onClose}
      size="xl"
    >
      <div className="attendance-form-body">

        {/* QUICK APPLY SETTINGS */}
        <div className="form-section-card">
          <div className="section-title-premium"><FaBolt /> Quick Apply Settings</div>
          <div className="quick-apply-row">
            <div className="quick-apply-col">
              <label>Attendance Date *</label>
              <DatePicker value={date} onChange={setDate} />

              <label style={{ marginTop: 8 }}>Quick Apply Status</label>
              <Dropdown
                options={AttendanceStatusFormOptions}
                value={globalStatus}
                onChange={setGlobalStatus}
                placeholder="Status"
              />
              <button type="button" className="secondary-btn full-width" onClick={applyStatusToAll}>
                Apply
              </button>
            </div>

            <div className="quick-apply-col">
              <label>Check-In Time</label>
              <TimePicker value={globalCheckIn} onChange={setGlobalCheckIn} />

              <label style={{ marginTop: 8 }}>Check-Out Time</label>
              <TimePicker value={globalCheckOut} onChange={setGlobalCheckOut} />

              <button type="button" className="secondary-btn full-width" onClick={applyTimesToAll}>
                Apply Times
              </button>
            </div>
          </div>
        </div>

        {/* EMPLOYEE ROWS */}
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaUsers /> Employees
            {rows.length > 0 && (
              <span className="selection-summary-chip" style={{ marginLeft: "auto" }}>
                {selectedCount} of {rows.length} selected
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 32 }}>Loading employees...</div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
              No active employees found.
            </div>
          ) : (
            <div style={{ maxHeight: "42vh", overflowY: "auto" }}>
              <table className="employee-table">
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th>Employee</th>
                    <th style={{ width: 150 }}>Status</th>
                    <th style={{ width: 130 }}>In Time</th>
                    <th style={{ width: 130 }}>Out Time</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const needsTime = [AttendanceStatus.PRESENT, AttendanceStatus.WORK_FROM_HOME, AttendanceStatus.HALF_DAY].includes(row.status);
                    return (
                      <tr key={row.employee_id} style={{ opacity: row.selected ? 1 : 0.5 }}>
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={row.selected}
                            onChange={(e) => updateRow(idx, "selected", e.target.checked)}
                          />
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{row.name}</div>
                          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{row.emp_id}</div>
                        </td>
                        <td>
                          <div style={{ width: "100%" }}>
                            <Dropdown
                              options={AttendanceStatusFormOptions}
                              value={row.status}
                              onChange={(val) => updateRow(idx, "status", val)}
                              placeholder="Status"
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ width: "100%", opacity: needsTime ? 1 : 0.5, pointerEvents: needsTime ? "auto" : "none" }}>
                            <TimePicker
                              value={needsTime ? row.check_in_time : ""}
                              onChange={(val) => updateRow(idx, "check_in_time", val)}
                              disabled={!needsTime}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ width: "100%", opacity: needsTime ? 1 : 0.5, pointerEvents: needsTime ? "auto" : "none" }}>
                            <TimePicker
                              value={needsTime ? row.check_out_time : ""}
                              onChange={(val) => updateRow(idx, "check_out_time", val)}
                              disabled={!needsTime}
                            />
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            placeholder="Optional note"
                            value={row.remarks}
                            onChange={(e) => updateRow(idx, "remarks", e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      <div className="modal-footer" style={{ marginLeft: -26, marginRight: -26, marginBottom: -22, marginTop: 20 }}>
        <div className="modal-actions">
          <button
            type="button"
            className="primary"
            disabled={submitting || selectedCount === 0}
            onClick={handleSubmit}
          >
            {submitting ? "Submitting..." : `Submit Bulk Attendance (${selectedCount})`}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkAttendanceModal;